import { Element, ElementType } from '@ritabygglov/core';
import CanvasSession from '../../services/CanvasSession';
import { AppState, SelectionMode, SelectionModes, ViewBox } from '../../types';
import Elements from './Elements';

const sortElements = (elements: AppState['elements']) =>
  Object.values(elements).sort((a, b) => {
    const val = a.renderingOrder - b.renderingOrder;

    if (val !== 0) return val;
    return a.id.localeCompare(b.id);
  });

const isSquareLike = (element: Element) =>
  element.type === ElementType.Rect ||
  element.type === ElementType.Text ||
  element.type === ElementType.Triangle ||
  element.type === ElementType.Trapezoid ||
  element.type === ElementType.Angle;

export interface CanvasElementsProps {
  elements: AppState['elements'];
  selectedElements: string[];
  hoverElement: string | null;
  selectionMode: SelectionMode;
  session: CanvasSession;
  scalingFactor: number;
  viewBox: ViewBox;
}

/**
 * Builds the props for every element renderer. Kept separate from Canvas so the
 * render-cost suite can exercise the real prop construction — React.memo only
 * bails out when every prop keeps its identity, and that depends entirely on
 * what this function passes down.
 */
export const CanvasElements = ({
  elements,
  selectedElements,
  hoverElement,
  selectionMode,
  session,
  scalingFactor,
  viewBox,
}: CanvasElementsProps) => {
  const isSoleSelection = selectedElements.length === 1;

  return (
    <>
      {sortElements(elements).map((e) => {
        const isSelected = selectedElements.includes(e.id);
        const isHovering = !isSelected && e.id === hoverElement;
        const classes = `${e.state}`;

        if (isSquareLike(e)) {
          return (
            <Elements.Square
              key={e.id}
              isSelected={isSelected}
              isSoleSelection={isSoleSelection}
              isHovering={isHovering}
              classes={classes}
              selectionMode={selectionMode}
              element={e}
              session={session}
              scalingFactor={scalingFactor}
              viewBox={viewBox}
            />
          );
        }

        if (e.type === ElementType.Symbol) {
          return (
            <Elements.Symbol
              key={e.id}
              isSelected={isSelected}
              isSoleSelection={isSoleSelection}
              isHovering={isHovering}
              viewBox={viewBox}
              classes={classes}
              element={e}
              session={session}
              scalingFactor={scalingFactor}
            />
          );
        }

        if (e.type === ElementType.Group) {
          return (
            <Elements.Group
              key={e.id}
              isSelected={isSelected}
              isHovering={isHovering}
              isEditing={
                selectionMode.type === SelectionModes.GroupEditing &&
                selectionMode.groupId === e.id
              }
              element={e}
              elements={elements}
            />
          );
        }

        if (e.type === ElementType.Fixture) {
          return (
            <Elements.Fixture
              key={e.id}
              isSelected={isSelected}
              isSoleSelection={isSoleSelection}
              isHovering={isHovering}
              viewBox={viewBox}
              classes={classes}
              element={e}
              session={session}
              scalingFactor={scalingFactor}
            />
          );
        }

        return (
          <Elements.Polyline
            key={e.id}
            isSelected={isSelected}
            isSoleSelection={isSoleSelection}
            isHovering={isHovering}
            classes={classes}
            element={e}
            session={session}
            scalingFactor={scalingFactor}
            viewBox={viewBox}
          />
        );
      })}
    </>
  );
};
